import {
  ChangeEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
} from 'downshift';

import {
  mergeRefs,
  useControlled,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import useFilteredItems from './useFilteredItems';
import useGroupedItems from './useGroupedItems';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  identityFn,
  isGroupHeader,
  wrapGetItemProps,
} from '../helpers';
import {
  GenericItem,
  GroupHeader,
  InputItemGroup,
  UseAutocompleteProps,
} from '../types';

/**
 * A hook used to manage all logic needed for an Autocomplete (searchable) dropdown.
 */
const useAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement | HTMLLabelElement,
>({
  items,
  groupedItems,
  isItemDisabled = defaultIsItemDisabled,
  itemToString = defaultLabelGetter,
  defaultHighlightedIndex = 0,
  target,
  onInputValueChange: onInputValueChangeProp,
  layoutDependency,
  getFilteredItems,
  getMenuProps: getMenuPropsProp,
  initialInputValue,
  stateReducer: stateReducerProp,
  ...props
}: UseAutocompleteProps<TItem, TTarget>) => {
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const targetRef = useForwardedRef(target || null);

  const [inputValue, setInputValue] = useControlled<string>(
    initialInputValue || '',
    props.inputValue,
  );

  const {
    flatItems,
    indexGetter: groupedIndexGetter,
    getMenuItems,
  } = useGroupedItems({
    maybeGroupedItems,
  });

  const { filteredItems } = useFilteredItems<TItem>({
    items: flatItems,
    getFilteredItems,
    itemToString,
    inputValue,
  });

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      switch (type) {
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          if (changes.isOpen && state.selectedItem) {
            // if the user is opening the menu while an item it selected, then let's make sure
            // that the highlighted index is set to the selected index
            return {
              ...changes,
              highlightedIndex: filteredItems.indexOf(state.selectedItem),
            };
          } else {
            return changes;
          }
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.FunctionSelectItem:
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          return {
            ...changes,
            inputValue: '', // remove user entered input value on selection or menu close
          };
        default:
          return changes;
      }
    },
    [filteredItems],
  );

  const onInputValueChange = useCallback(
    (args: UseComboboxStateChange<TItem>) => {
      if (
        // This type gets triggered on render in multi autocomplete scenarios which provide selectedItem as null
        // This sets the input value to '' and breaks `initialInputValue`
        args.type !==
        useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem
      ) {
        if (props.inputValue === undefined) {
          // controlled inputValue should take priority
          setInputValue(args.inputValue || '');
        }
      }

      onInputValueChangeProp?.(args);
    },
    [onInputValueChangeProp, props.inputValue, setInputValue],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getInputProps: rawGetInputProps,
    getItemProps: rawGetItemProps,
    getToggleButtonProps: rawGetToggleButtonProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    ...useComboboxReturnValue
  } = useCombobox<TItem>({
    items: filteredItems,
    itemToString,
    initialInputValue,
    inputValue,
    onInputValueChange,
    defaultHighlightedIndex,
    stateReducer,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
    ...useOverlayReturnValue
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    target: targetRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const onComposition = useRef(false);

  // https://github.com/facebook/react/issues/3926#issuecomment-929799564
  const getOnChangeWithCompositionSupport = useCallback(
    ({
      onChangeProp,
    }: {
      onChangeProp: (e: ChangeEvent<HTMLInputElement>) => void;
    }) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event?.target?.value);

        // IME method start
        if (event.type === 'compositionstart') {
          onComposition.current = true;
          return;
        }

        // IME method end
        if (event.type === 'compositionend') {
          onComposition.current = false;
        }

        // handle parent onChange
        if (!onComposition.current) {
          onChangeProp?.(event);
        }
      },
    [setInputValue],
  );

  const getMenuProps: typeof rawGetMenuProps = useCallback(
    (args) =>
      rawGetMenuProps(
        (getMenuPropsProp || identityFn)({ ...overlayProps, ...args }),
      ),
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  );

  const getInputProps: typeof rawGetInputProps = useCallback(
    (args) => {
      const { onChange: rawOnChange, ...inputProps } = rawGetInputProps(args);

      const onChange = getOnChangeWithCompositionSupport({
        onChangeProp: rawOnChange,
      });

      return {
        onChange,
        onCompositionStart: onChange,
        onCompositionEnd: onChange,
        ...inputProps,
      };
    },
    [getOnChangeWithCompositionSupport, rawGetInputProps],
  );

  const getToggleButtonProps: typeof rawGetToggleButtonProps = useCallback(
    (props) => {
      return rawGetToggleButtonProps({ tabIndex: 0, ...props });
    },
    [rawGetToggleButtonProps],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(
    () => getMenuItems(filteredItems) as MenuItems,
    [filteredItems, getMenuItems],
  );

  const defaultIndexGetter = useCallback(
    (item: TItem) => menuItems.indexOf(item),
    [menuItems],
  );

  const getItemProps: typeof rawGetItemProps = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        isItemDisabled,
        highlightedIndex: useComboboxReturnValue.highlightedIndex,
        selectedItem: useComboboxReturnValue.selectedItem,
        indexGetter: groupedIndexGetter || defaultIndexGetter,
      })(args),
    [
      defaultIndexGetter,
      groupedIndexGetter,
      isItemDisabled,
      rawGetItemProps,
      useComboboxReturnValue.highlightedIndex,
      useComboboxReturnValue.selectedItem,
    ],
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  return {
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen: useOverlayIsOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    menuItems,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from controlProps
     */
    getInputProps,
    /**
     * @deprecated use value returned from menuProps
     */
    getMenuProps,
    controlProps: {
      ref: targetRef,
      isAutocomplete: true,
      isOpen: useOverlayIsOpen,
      getInputProps,
      getToggleButtonProps,
      openMenu: useComboboxReturnValue.openMenu,
      selectedItem: useComboboxReturnValue.selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: true,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex: useComboboxReturnValue.highlightedIndex,
      selectedItem: useComboboxReturnValue.selectedItem,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
    },
    selectItem,
    ...useComboboxReturnValue,
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    targetProps: { ref: targetRef },
    ...useOverlayReturnValue,
  };
};

export default Object.assign(useAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
});
